import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Camera, Plus, X, Linkedin, Instagram, Music2, Briefcase, Cake, MapPin, Save, Settings, LogOut, Lock, Home, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MediaUploader } from "../components/MediaUploader";
import { BottomSheet } from "../components/BottomSheet";
import { InviteSheet } from "../components/InviteSheet";
import { Avatar } from "./Hangouts";
import { UserPlus } from "lucide-react";

const inputStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" };
const cls = "w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]";

export default function ProfilePage() {
  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const goBack = useSafeBack("/lineage");
  const [profile, setProfile] = useState(null);
  const [relOptions, setRelOptions] = useState({ users: [], ancestors: [] });
  const [saving, setSaving] = useState(false);
  const [relSheet, setRelSheet] = useState(null); // 'parent' | 'child' | null
  const [form, setForm] = useState(null);
  const [bdEditing, setBdEditing] = useState(false);
  const [bdRequest, setBdRequest] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRelative, setPendingRelative] = useState(null);

  const computeAge = (bd) => {
    if (!bd) return null;
    const b = new Date(bd);
    const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a -= 1;
    return a;
  };

  const load = useCallback(async () => {
    const [{ data: p }, { data: opts }] = await Promise.all([
      api.get("/profile"), api.get("/relatives/options"),
    ]);
    setProfile(p);
    setRelOptions(opts);
    setForm({
      name: p.name || "", photo: p.photo || "", bio: p.bio || "", birthday: p.birthday || "",
      occupation: p.occupation || "", birth_city: p.birth_city || "",
      current_city: p.current_city || "", hobbies: p.hobbies || "",
      socials: { linkedin: p.socials?.linkedin || "", instagram: p.socials?.instagram || "", tiktok: p.socials?.tiktok || "" },
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", form);
      if (!profile.birthday && form.birthday) {
        await api.post("/profile/birthday", { birthday: form.birthday });
      }
      await refreshUser();
      toast.success("Profile saved");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submitBirthdayChange = async () => {
    if (!bdRequest) return;
    try {
      const { data } = await api.post("/profile/birthday", { birthday: bdRequest });
      if (data.status === "requested") toast.success("Change request sent to your admins for approval");
      else if (data.status === "set") toast.success("Birthday saved");
      else toast("No change made");
      setBdEditing(false);
      setBdRequest("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addRelation = async (relative, subtype) => {
    try {
      await api.post("/profile/relations", { relative_id: relative.id, relative_kind: relative.kind, relation: relSheet, subtype });
      setRelSheet(null); setPendingRelative(null);
      toast.success("Added — admins were asked to update the family tree");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeRelation = async (id) => {
    try { await api.delete(`/profile/relations/${id}`); toast.success("Removal request sent for admin approval"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!profile || !form) {
    return <div className="grid place-items-center" style={{ minHeight: "60vh" }}><Loader2 className="animate-spin gold-text" /></div>;
  }

  // A person may hold only one role at a time — exclude anyone already assigned to any relation.
  const assignedIds = new Set([
    profile.id,
    ...profile.parents.map((r) => r.id),
    ...profile.children.map((r) => r.id),
    ...(profile.aunts_uncles || []).map((r) => r.id),
  ]);
  const memberOptions = relOptions.users.filter((m) => !assignedIds.has(m.id));
  const ancestorOptions = relOptions.ancestors.filter((a) => !assignedIds.has(a.id));
  const SUBTYPES = {
    parent: [["mom", "Mom"], ["dad", "Dad"], ["stepmom", "Stepmom"], ["stepdad", "Stepdad"]],
    child: [["son", "Son"], ["daughter", "Daughter"], ["other", "Other"]],
    aunt_uncle: [["aunt", "Aunt"], ["uncle", "Uncle"]],
  };

  const social = (key, Icon, ph) => (
    <div className="flex items-center gap-2 rounded-xl px-3" style={inputStyle}>
      <Icon size={16} style={{ color: "var(--text-muted)" }} />
      <input value={form.socials[key]} onChange={(e) => setForm({ ...form, socials: { ...form.socials, [key]: e.target.value } })}
        placeholder={ph} data-testid={`profile-${key}-input`}
        className="flex-1 bg-transparent py-3 text-sm outline-none" style={{ color: "var(--text-primary)" }} />
    </div>
  );

  return (
    <div style={{ paddingBottom: 40 }}>
      <div className="sticky top-0 z-40 glass hairline px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={goBack} data-testid="profile-back-btn"><ArrowLeft size={22} /></button>
          <h1 className="font-serif-display text-2xl">My Profile</h1>
        </div>
        <motion.button whileTap={{ scale: 0.95 }} onClick={save} disabled={saving}
          data-testid="profile-save-btn"
          className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5"
          style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </motion.button>
      </div>

      <div className="px-5 py-6 lg:px-10 lg:py-10">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="h-28 w-28 rounded-full overflow-hidden gold-border grid place-items-center" style={{ background: "var(--bg-elevated)" }}>
              {form.photo ? <img src={fileUrl(form.photo)} alt="" className="h-full w-full object-cover" />
                : <span className="font-serif-display text-4xl gold-text">{(form.name || "?").charAt(0)}</span>}
            </div>
          </div>
          <div className="w-44 mt-3">
            <MediaUploader label="Change photo" testid="profile-photo-upload" onUploaded={(p) => setForm((f) => ({ ...f, photo: p }))} />
          </div>
        </div>

        <div className="mt-6 space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-4">
          <Field label="Name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="profile-name-input" className={cls} style={inputStyle} placeholder="Full name" />
          </Field>
          <Field label="Occupation" icon={Briefcase}>
            <input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })}
              data-testid="profile-occupation-input" className={cls} style={inputStyle} placeholder="e.g. Architect" />
          </Field>
          <div className="grid grid-cols-2 gap-3 lg:col-span-2">
            <Field label="Birthday" icon={Cake}>
              {profile.birthday ? (
                <div>
                  <div className="flex items-center justify-between rounded-xl px-3 py-3 min-w-0" style={inputStyle} data-testid="birthday-locked">
                    <span className="text-sm truncate">{profile.birthday}{computeAge(profile.birthday) != null ? ` · ${computeAge(profile.birthday)}y` : ""}</span>
                    <Lock size={13} style={{ color: "var(--text-muted)" }} />
                  </div>
                  {!bdEditing ? (
                    <button onClick={() => { setBdEditing(true); setBdRequest(profile.birthday); }}
                      data-testid="request-birthday-change-btn" className="text-[11px] gold-text mt-1">Request change</button>
                  ) : (
                    <div className="mt-2 space-y-2">
                      <input type="date" value={bdRequest} onChange={(e) => setBdRequest(e.target.value)}
                        data-testid="birthday-change-input" className={`${cls} min-w-0`} style={inputStyle} />
                      <div className="flex gap-2">
                        <button onClick={submitBirthdayChange} data-testid="submit-birthday-change-btn"
                          className="flex-1 rounded-lg py-2 text-xs font-semibold" style={{ background: "var(--accent)", color: "var(--bg-base)" }}>Send request</button>
                        <button onClick={() => setBdEditing(false)} className="rounded-lg px-3 py-2 text-xs" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                  data-testid="profile-birthday-input" className={`${cls} min-w-0`} style={inputStyle} />
              )}
            </Field>
            <Field label="Birth city" icon={MapPin}>
              <input value={form.birth_city} onChange={(e) => setForm({ ...form, birth_city: e.target.value })}
                data-testid="profile-birthcity-input" className={`${cls} min-w-0`} style={inputStyle} placeholder="City" />
            </Field>
          </div>
          <Field label="Current city" icon={Home}>
            <input value={form.current_city} onChange={(e) => setForm({ ...form, current_city: e.target.value })}
              data-testid="profile-currentcity-input" className={cls} style={inputStyle} placeholder="Where you live now" />
          </Field>
          <Field label="Hobbies & interests" icon={Sparkles}>
            <input value={form.hobbies} onChange={(e) => setForm({ ...form, hobbies: e.target.value })}
              data-testid="profile-hobbies-input" className={cls} style={inputStyle} placeholder="e.g. Cooking, hiking, chess" />
          </Field>
          <div className="lg:col-span-2">
            <Field label="Bio">
              <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3}
                data-testid="profile-bio-input" className={cls} style={inputStyle} placeholder="Tell your family about yourself…" />
            </Field>
          </div>

          <div className="text-xs uppercase tracking-widest pt-2 lg:col-span-2" style={{ color: "var(--text-muted)" }}>Social handles</div>
          {social("linkedin", Linkedin, "LinkedIn username")}
          {social("instagram", Instagram, "Instagram handle")}
          {social("tiktok", Music2, "TikTok handle")}
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          <RelationSection title="Parents" testid="parents" people={profile.parents}
            onAdd={() => setRelSheet("parent")} onRemove={removeRelation} />
          <RelationSection title="Children" testid="children" people={profile.children}
            onAdd={() => setRelSheet("child")} onRemove={removeRelation} />
          <RelationSection title="Aunts & Uncles" testid="aunts-uncles" people={profile.aunts_uncles || []}
            onAdd={() => setRelSheet("aunt_uncle")} onRemove={removeRelation} />
        </div>

        <div className="mt-8 space-y-2 lg:hidden">
          <button onClick={() => setInviteOpen(true)} data-testid="invite-family-btn"
            className="w-full card-surface rounded-2xl px-4 py-3.5 flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--overlay)" }}>
              <UserPlus size={17} className="gold-text" />
            </div>
            <span className="text-sm font-medium">Invite Family Members</span>
            <ArrowLeft size={16} className="ml-auto rotate-180" style={{ color: "var(--text-muted)" }} />
          </button>
          <button onClick={() => navigate("/settings")} data-testid="open-settings-btn"
            className="w-full card-surface rounded-2xl px-4 py-3.5 flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--overlay)" }}>
              <Settings size={17} className="gold-text" />
            </div>
            <span className="text-sm font-medium">Settings</span>
            <ArrowLeft size={16} className="ml-auto rotate-180" style={{ color: "var(--text-muted)" }} />
          </button>
          <button onClick={logout} data-testid="profile-logout-btn"
            className="w-full rounded-2xl px-4 py-3.5 flex items-center gap-3" style={{ background: "var(--overlay)" }}>
            <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--overlay)" }}>
              <LogOut size={17} style={{ color: "var(--destructive)" }} />
            </div>
            <span className="text-sm font-medium" style={{ color: "var(--destructive)" }}>Log Out</span>
          </button>
        </div>
      </div>

      <BottomSheet open={!!relSheet} onClose={() => { setRelSheet(null); setPendingRelative(null); }}
        title={relSheet === "parent" ? "Add a Parent" : relSheet === "child" ? "Add a Child" : "Add an Aunt / Uncle"}>
        {pendingRelative ? (
          <div>
            <button onClick={() => setPendingRelative(null)} className="flex items-center gap-2 text-sm mb-4" data-testid="relation-subtype-back" style={{ color: "var(--text-secondary)" }}>
              <ArrowLeft size={16} /> Back
            </button>
            <div className="flex items-center gap-3 mb-4">
              <Avatar name={pendingRelative.name} sm />
              <div className="text-sm font-medium">{pendingRelative.name}</div>
            </div>
            <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>How are they your relative?</p>
            <div className="grid grid-cols-2 gap-2">
              {(SUBTYPES[relSheet] || []).map(([val, label]) => (
                <button key={val} onClick={() => addRelation(pendingRelative, val)}
                  data-testid={`relation-subtype-${val}`}
                  className="rounded-xl px-4 py-3 text-sm font-medium card-surface">
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : (
        <>
        <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
          Choose a living member or an ancestor. Admins will be asked to add them to the family tree.
        </p>
        {memberOptions.length === 0 && ancestorOptions.length === 0 && (
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>No one else available to add.</p>
        )}
        {memberOptions.length > 0 && (
          <>
            <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>Members</div>
            <div className="space-y-2 mb-4">
              {memberOptions.map((m) => (
                <button key={m.id} onClick={() => setPendingRelative(m)}
                  data-testid={`relation-option-${m.id}`}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: "var(--overlay)" }}>
                  <Avatar name={m.name} sm />
                  <div className="text-sm font-medium">{m.name}</div>
                </button>
              ))}
            </div>
          </>
        )}
        {ancestorOptions.length > 0 && (
          <>
            <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>Ancestors</div>
            <div className="space-y-2">
              {ancestorOptions.map((a) => (
                <button key={a.id} onClick={() => setPendingRelative(a)}
                  data-testid={`relation-option-${a.id}`}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: "var(--overlay)" }}>
                  <Avatar name={a.name} sm />
                  <div className="text-sm font-medium">{a.name}</div>
                  <span className="ml-auto text-[10px] uppercase tracking-widest gold-text">In Memoriam</span>
                </button>
              ))}
            </div>
          </>
        )}
        </>
        )}
      </BottomSheet>
      <InviteSheet open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide flex items-center gap-1 mb-1" style={{ color: "var(--text-muted)" }}>
        {Icon && <Icon size={11} />} {label}
      </label>
      {children}
    </div>
  );
}

function RelationSection({ title, testid, people, onAdd, onRemove }) {
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{title}</div>
        <button onClick={onAdd} data-testid={`add-${testid}-btn`} className="flex items-center gap-1 text-xs gold-text">
          <Plus size={14} /> Add
        </button>
      </div>
      {people.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>None added yet.</div>
      ) : (
        <div className="space-y-2">
          {people.map((p) => (
            <div key={p.relation_id} data-testid={`${testid}-item-${p.id}`}
              className="flex items-center justify-between card-surface rounded-xl px-3 py-2.5">
              <div className="flex items-center gap-2.5"><Avatar name={p.name} sm />
                <div>
                  <div className="text-sm font-medium">{p.name}</div>
                  {p.subtype && <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{p.subtype}</div>}
                </div>
                {p.kind === "ancestor" && <span className="text-[10px] uppercase tracking-widest gold-text">In Memoriam</span>}
              </div>
              <button onClick={() => onRemove(p.relation_id)} data-testid={`remove-relation-${p.relation_id}`}>
                <X size={16} style={{ color: "var(--destructive)" }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
